"use client";

import {
  useParams,
  usePathname,
  useRouter,
  useSearchParams
} from "next/navigation";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";

export type TabsProps = {
  id: string;
  title: (() => React.ReactNode) | React.ReactNode;
  render: (s?: unknown) => React.ReactNode;
  allowOnlyForStaff?: boolean;
  /** Optional URL to navigate to when this tab is selected */
  url?: string;
  [key: string]: unknown;
};

export interface UseRouterTabsOptions {
  /** Enable router integration (default: true) */
  enableRouter?: boolean;
  /** Use hash-based routes like #tab-xxx (default: false). If false, uses URL params or paths */
  useHashRoutes?: boolean;
  /** Base URL for non-hash routes (e.g., "/settings/offices") */
  baseUrl?: string;
  /** URL parameter key for reading tab value from dynamic route params (default: "tabValue"). Used when useHashRoutes is false */
  urlParamKey?: string;
  /** Use shallow routing (pushState) to avoid re-renders when changing tabs (default: true) */
  shallowRouting?: boolean;
}

export function useRouterTabs(
  tabs: TabsProps[],
  options: UseRouterTabsOptions = {}
) {
  const {
    enableRouter = true,
    useHashRoutes = false,
    baseUrl = "",
    urlParamKey = "tabValue",
    shallowRouting = true
  } = options;

  const router = useRouter();
  const nextPathname = usePathname();
  const params = useParams();
  const searchParams = useSearchParams();

  // Track current pathname (updated by both Next.js navigation and pushState)
  const [currentPathname, setCurrentPathname] = useState<string>(() => {
    if (typeof window !== "undefined") {
      return window.location.pathname;
    }
    return nextPathname ?? "";
  });

  // Sync with Next.js pathname on navigation
  useEffect(() => {
    if (nextPathname && nextPathname !== currentPathname) {
      setCurrentPathname(nextPathname);
    }
  }, [nextPathname, currentPathname]);

  // Build the full path including hash from window (since Next.js doesn't provide hash)
  const [hash, setHash] = useState("");

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    setHash(window.location.hash);

    const handleHashChange = () => {
      setHash(window.location.hash);
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  // Find the current tab by matching pathname against tab URLs or URL params
  const getTabIdFromUrl = useCallback((): string | undefined => {
    // For hash-based routes
    if (useHashRoutes) {
      const selectedTab = hash.match(/#tab-([A-Za-z\d\-_]+)/);
      if (selectedTab) {
        const hashTabId = selectedTab[1];
        // Verify tab exists
        if (tabs.some((t) => t.id === hashTabId)) {
          return hashTabId;
        }
      }
      return tabs[0]?.id;
    }

    // PRIORITY 1: Match current pathname against tab URLs
    // This is the most reliable method since tabs define their exact URLs
    const matchingTab = tabs.find((tab) => {
      if (tab.url) {
        // Remove query string and hash from tab URL for comparison
        const tabPathname = tab.url.split("?")[0].split("#")[0];
        return currentPathname === tabPathname;
      }
      return false;
    });
    if (matchingTab) {
      return matchingTab.id;
    }

    // PRIORITY 2: Check dynamic route params (e.g., [tabValue])
    const paramValue = params?.[urlParamKey];
    if (paramValue) {
      const tabId = Array.isArray(paramValue) ? paramValue[0] : paramValue;
      if (tabId && tabs.some((t) => t.id === tabId)) {
        return tabId;
      }
    }

    // PRIORITY 3: Check search params (query string)
    const searchValue = searchParams?.get(urlParamKey);
    if (searchValue && tabs.some((t) => t.id === searchValue)) {
      return searchValue;
    }

    // PRIORITY 4: Extract tab from pathname using baseUrl
    if (baseUrl) {
      const basePath = baseUrl.replace(/\/$/, "");
      if (currentPathname?.startsWith(basePath)) {
        const remaining = currentPathname.slice(basePath.length);
        const tabId = remaining.replace(/^\//, "");
        if (tabId && tabs.some((t) => t.id === tabId)) {
          return tabId;
        }
      }
    }

    // PRIORITY 5: Check last segment of pathname against tab IDs
    if (currentPathname) {
      const segments = currentPathname.split("/").filter(Boolean);
      const lastSegment = segments[segments.length - 1];
      if (lastSegment && tabs.some((t) => t.id === lastSegment)) {
        return lastSegment;
      }
    }

    // Default to first tab
    return tabs[0]?.id;
  }, [
    useHashRoutes,
    hash,
    tabs,
    currentPathname,
    params,
    urlParamKey,
    searchParams,
    baseUrl
  ]);

  const [value, setValue] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  // Track if we're doing a programmatic tab change (to skip effect)
  const isUserNavigationRef = useRef(false);

  useLayoutEffect(() => {
    setReady(true);
  }, []);

  // Update value when URL changes (initial load or Next.js navigation)
  useEffect(() => {
    // Skip if this is a programmatic change (user clicked tab)
    if (isUserNavigationRef.current) {
      isUserNavigationRef.current = false;
      return;
    }

    // When router is disabled, only set initial value, don't sync with URL changes
    if (!enableRouter && value !== null) {
      return;
    }

    const tabId = getTabIdFromUrl();
    if (tabId && tabId !== value) {
      setValue(tabId);
    }
  }, [currentPathname, getTabIdFromUrl, value, enableRouter]);

  // Handle browser back/forward - read directly from URL
  const handlePopStateNavigation = useCallback(() => {
    const pathname = window.location.pathname;

    // Find matching tab by URL
    const matchingTab = tabs.find((tab) => {
      if (tab.url) {
        const tabPathname = tab.url.split("?")[0].split("#")[0];
        return pathname === tabPathname;
      }
      return false;
    });

    if (matchingTab) {
      setValue(matchingTab.id);
    } else {
      // Fallback: check last segment
      const segments = pathname.split("/").filter(Boolean);
      const lastSegment = segments[segments.length - 1];
      if (lastSegment && tabs.some((t) => t.id === lastSegment)) {
        setValue(lastSegment);
      }
    }
  }, [tabs]);

  // Register popstate handler (only when router is enabled)
  useEffect(() => {
    if (typeof window === "undefined" || !enableRouter) {
      return;
    }
    window.addEventListener("popstate", handlePopStateNavigation);
    return () => {
      window.removeEventListener("popstate", handlePopStateNavigation);
    };
  }, [handlePopStateNavigation, enableRouter]);

  const onValueChange = (newValue: string) => {
    // Set value immediately for instant UI feedback
    setValue(newValue);
    // Mark as programmatic change to skip the effect
    isUserNavigationRef.current = true;

    if (enableRouter) {
      // Check if the tab has a custom URL defined
      const tab = tabs.find((t) => t.id === newValue);
      let newUrl: string;

      if (tab?.url) {
        newUrl = tab.url;
      } else if (useHashRoutes) {
        // For hash routes, update the hash without full navigation
        if (typeof window !== "undefined") {
          window.location.hash = `tab-${newValue}`;
        }
        return;
      } else {
        const basePath = baseUrl.replace(/\/$/, "");
        newUrl = `${basePath}/${newValue}`;
      }

      // Use shallow routing (pushState) to avoid re-renders, or regular navigation
      if (shallowRouting && typeof window !== "undefined") {
        // Just update the URL, no need to update state since we already set the value
        window.history.pushState({}, "", newUrl);
      } else {
        router.push(newUrl);
      }
    }
  };

  return {
    value,
    onValueChange,
    isReady: ready,
    defaultValue: tabs[0]?.id
  };
}
